import React, { useEffect, useRef, useState } from 'react';
import { useForm, useSubmit } from '@balance-web/forms';

import type { MicrosostAuthenticationContext } from '../../../utils/analytics';
import { events, track } from '../../../utils/analytics';
import { useMutateLogin } from '../../../services/queryClient';
import { useMutateMicrosoftLogin } from '../../../services/queryClient/useMutateMicrosoftLogin';
import { useAuth } from '../../../context/useAuth';
import { useMicrosoftAuthentication } from '../../hooks/useMicrosoftAuthentication';
import { useMutateGoogleLogin } from '../../../services/queryClient/useMutateGoogleLogin';
import { useGoogleAuthentication } from '../../hooks/useGoogleAuthentication';
import type { GoogleSignupProps } from '../../../lib/api';
import { getErrorMessageFromErrorType, isError } from '../../../utils/utils';
import { mfaInfo, mfaResignToken, validateMfaToken } from '../../../lib/api';
import { useClient } from '../../hooks';
import { useMFARequirementCheck } from '../../hooks/useMFARequirementCheck';

import type { SubmitError, SubmitErrors } from './types';
import type { SignInComponentProps } from './types';
import { SignInForm } from './form';
import { SignInFormSchema } from './SignInFormSchema';

export const SignInComponent = ({
  resetPasswordSuccess,
  onRedirectToClient,
  navigateToResetPassword,
  navigateToSignup,
  navigateToForgotPassword,
  navigateToVerifyMfa,
  navigateToSetupMfa,
  loggedout,
}: SignInComponentProps) => {
  const { setUser, user } = useAuth();
  const login = useMutateLogin();
  const microsoftlogin = useMutateMicrosoftLogin();
  const googleSignin = useMutateGoogleLogin();
  const { getMfaToken, redirectURL } = useClient();
  const { validatingMFAToken } = useMFARequirementCheck(user as any);

  const [signInError, setSignInError] = useState<SubmitErrors>();
  const [validatingLogin, setValidatingLogin] = useState(false);
  const [inActive, setInactive] = useState(loggedout);
  const [message, setMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<
    'reset_password' | 'register' | 'none'
  >('none');

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const handleMicrosoftLogin = async (
    err: any,
    token: string,
    ADAL: MicrosostAuthenticationContext,
    redirecturl: string
  ) => {
    if (!err) {
      try {
        const user = ADAL.getCachedUser();
        const email = user.profile.unique_name;
        microsoftlogin.mutate(
          {
            token,
            email,
          },
          {
            onSuccess: (data) => {
              setValidatingLogin(false);
              setUser(data);

              track({
                action: events.USER_LOGGED_IN,
                params: {
                  user_id: data.userid,
                  source: 'Identity-Microsoft',
                },
              });

              if (!data.mfaRequired) {
                onRedirectToClient(redirecturl, data.token);
              }
            },
            onError: (err: any) => {
              setSignInError(err?.message);
              setValidatingLogin(false);
            },
          }
        );
      } catch (e) {}
    } else {
      console.error('error: ' + err);
    }
  };

  const handleGoogleLogin = async (data: GoogleSignupProps) => {
    googleSignin.mutate(data, {
      onSuccess: (user) => {
        setValidatingLogin(false);
        setUser(user);
        track({
          action: events.USER_LOGGED_IN,
          params: {
            user_id: user.userid,
            source: 'Identity-Google',
          },
        });

        if (!user.mfaRequired) {
          onRedirectToClient(data.redirectURL, user.token);
        }
      },
      onError: (err: any) => {
        setSignInError(err?.message);
        setValidatingLogin(false);
      },
    });
  };

  const { ADAL } = useMicrosoftAuthentication(handleMicrosoftLogin);
  const { googleLogin } = useGoogleAuthentication(handleGoogleLogin);

  const form = useForm(SignInFormSchema);
  useEffect(() => {
    setInactive(loggedout);
  }, [loggedout]);
  useEffect(() => {
    setSignInError(undefined);
  }, [form.value.emailOrUsername, form.value.password]);

  const handleSubmit = useSubmit(form, async (value) => {
    setMessage('');
    setStatusType('none');
    setValidatingLogin(true);
    login.mutate(
      {
        username: value.emailOrUsername,
        password: value.password,
      },
      {
        onSuccess: async (result) => {
          if (isError(result)) {
            setValidatingLogin(false);
            setInactive(false);
            setMessage(
              getErrorMessageFromErrorType(result.errorType, result.message)
                .message
            );
            setStatusType(
              getErrorMessageFromErrorType(result.errorType, result.message)
                .statusType
            );
            return;
          }

          /**
           * If mfa is not required, return to the client
           */

          if (!result.mfaRequired) {
            onRedirectToClient(redirectURL, result.token);
            return;
          }

          const mfaInfoResult = await mfaInfo(result.token);
          if (isError(mfaInfoResult)) {
            setMessage(mfaInfoResult.message);
            return;
          }

          setUser({
            ...result,
            ...mfaInfoResult,
          });

          track({
            action: events.USER_LOGGED_IN,
            params: {
              user_id: result.userid,
              source: 'Identity',
            },
          });

          const doesRequireMFAAndMFAEnabled =
            result.mfaRequired && result.mfaEnabled;
          const doesRequireMFAAndMFANotEnabled =
            result.mfaRequired && !result.mfaEnabled;
          const mfaToken = getMfaToken();

          /**
           * MFA is required but mfaToken is not available in cookies then we don't have to do the API call.
           */

          if (doesRequireMFAAndMFAEnabled && !mfaToken) {
            navigateToVerifyMfa();
            return;
          }

          /**
           * MFA is required, mfaToken is not available in cookies and mfa is not enabled then we don't have to do the API call.
           */

          if (doesRequireMFAAndMFANotEnabled && !mfaToken) {
            navigateToSetupMfa();
            return;
          }

          /**
           * Now we know wha
           */
          const mfaTokenValidationResult = await validateMfaToken(
            getMfaToken(),
            result.token
          );

          const isMfaTokenInvalid = isError(mfaTokenValidationResult);

          /**
           * When user has set up their mfa, let's take them to mfa verify page
           */

          if (doesRequireMFAAndMFAEnabled && isMfaTokenInvalid) {
            navigateToVerifyMfa();
            return;
          }

          /**
           * When user is required to do the MFA but not enabled, let's take them to
           */

          if (doesRequireMFAAndMFANotEnabled && isMfaTokenInvalid) {
            navigateToSetupMfa();
            return;
          }

          /**
           * When above condition does not match then let's go to the client
           * But resign the token with the existing mfa token.
           */

          const mfaResignTokenResult = await mfaResignToken({
            mfaToken,
            accessToken: result.token,
          });

          if (isError(mfaResignTokenResult)) {
            if (doesRequireMFAAndMFAEnabled) {
              navigateToVerifyMfa();
              return;
            }

            if (doesRequireMFAAndMFANotEnabled) {
              navigateToSetupMfa();
              return;
            }

            return;
          }

          onRedirectToClient(redirectURL, mfaResignTokenResult.token);
          setValidatingLogin(false);
          setInactive(false);
        },
        onError: (err: any) => {
          setSignInError(err?.message);
          setValidatingLogin(false);
        },
      }
    );
  });

  const getLoginError = (errorType: SubmitErrors): SubmitError => {
    // These errors are coming from the API
    switch (errorType) {
      case 'Unable to validate user':
        return {
          tone: 'critical',
          description:
            'The email or password you’ve entered is incorrect. Please try again.<br /> Your account will be locked after repeated failed attempts.',
        };
      case 'User not verified':
        return {
          tone: 'critical',
          description:
            'This email address has not been verified, check your inbox for the verification email. Reset your password if you have lost the verification email.',
        };
      case 'Password attempts exceeded':
        return {
          tone: 'critical',
          description:
            'You have failed to sign in many times, for your safety, your account has been locked.<br/> Please reset your password to unlock your account.',
          actions: {
            primary: {
              label: 'Reset password',
              action: navigateToForgotPassword,
            },
            secondary: {
              label: 'Help with sign in',
              action: () => {
                return window.open(process.env.forgotEmailHelpLink as string);
              },
            },
          },
        };
      default:
        return {
          tone: 'critical',
          description: 'Something went wrong. Please try again later.',
        };
    }
  };
  return (
    <SignInForm
      form={form}
      loggedout={inActive}
      message={message || signInError}
      navigation={{
        navigateToCreateAccount: navigateToSignup,
        navigateToForgotPassword,
      }}
      passwordInputRef={passwordInputRef}
      resetPasswordSuccess={resetPasswordSuccess}
      SingleSignOnActions={{
        microsoft: ADAL && {
          onClick: async () => {
            try {
              ADAL.login();
            } catch (error) {
              throw error;
            }
          },
        },
        google: {
          onClick: googleLogin,
        },
      }}
      statusType={statusType}
      submitAction={{
        loading: login.isLoading || validatingLogin || validatingMFAToken,
        onClick: handleSubmit,
        error: signInError && getLoginError(signInError),
        disabled: signInError === 'Password attempts exceeded',
      }}
    />
  );
};
